import { useDispatch, useSelector } from "react-redux"
import { setPublicKey } from "../redux/actions"
import { getPublicApiKey } from "../services/endpointUtils/auth"
import { useCallback, useEffect, useRef, useState } from "react"
import { isEmpty } from "./commonFunction"
import axios from "axios"

export const isValidPublicKey = (key) => {
  return !isEmpty(key) && typeof key === "string" && key.includes("BEGIN")
}

const useFetchPublicApiKey = () => {
  const dispatch = useDispatch()
  const publicApiKey = useSelector((state) => state?.userSession?.publicApiKey)
  const cancelApi = useRef(null)
  const [isKeyLoading, setIsKeyLoading] = useState(() => !isValidPublicKey(publicApiKey))
  const [fetchFailed, setFetchFailed] = useState(false)
  // Bumping this re-runs the fetch in place. Callers used to trigger a full
  // window.location.reload() to retry, which re-downloaded and re-parsed the whole bundle.
  const [retryToken, setRetryToken] = useState(0)

  const isKeyReady = isValidPublicKey(publicApiKey)

  const retryFetchKey = useCallback(() => {
    setFetchFailed(false)
    setRetryToken((token) => token + 1)
  }, [])

  useEffect(() => {
    if (isKeyReady) {
      setIsKeyLoading(false)
      setFetchFailed(false)
      return
    }

    let isCancelled = false
    setIsKeyLoading(true)
    setFetchFailed(false)

    if (cancelApi.current) {
      cancelApi.current.cancel("cancelled")
    }

    const CancelToken = axios.CancelToken
    cancelApi.current = CancelToken.source()

    getPublicApiKey({ cancelToken: cancelApi.current.token })
      .then((res) => {
        if (isCancelled || axios.isCancel(res) || res?.code === "ERR_CANCELED") {
          return
        }

        if (res?.status === 200 && isValidPublicKey(res.data)) {
          dispatch(setPublicKey(res.data))
          return
        }

        setFetchFailed(true)
      })
      .catch(() => {
        if (!isCancelled) {
          setFetchFailed(true)
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsKeyLoading(false)
        }
      })

    return () => {
      isCancelled = true
      cancelApi.current?.cancel("cancelled")
    }
  }, [publicApiKey, isKeyReady, dispatch, retryToken])

  return { isKeyReady, isKeyLoading, fetchFailed, retryFetchKey }
}

export default useFetchPublicApiKey
